import React from 'react';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { CancelRounded, PlayArrowRounded } from '@mui/icons-material';
import { ICourseData } from '../../types/course-data';
import { ModalDescription } from './modal-description';
import { LessonList } from './modal-lesson-list';
import { ModalMetadata } from './modal-metadata';
import { ModalVideo } from './modal-video';
import { DialogContentMobile } from './modal-content-mobile';
import { DashboardEventKeys, DashboardEvents } from '../../events';
import { EventBus } from '@/shared/utils';
import { routes } from 'src/shared/utils/routes';
import { adaptCourseTypeText } from 'src/shared/utils/adapt-course-type-text';

export const ModalDetails = ({
  documentId,
  eventBus,
  title,
  description,
  videoCount,
  duration,
  level,
  genres,
  instructors,
  trailerUrl,
  dialogIsOpen,
  dialogToggle,
  lessons,
  coursePercentage,
  imageSrc,
  colorScheme,
  courseType,
  trailerPlaybackId,
  trailerPlaybackToken,
}: ICourseData & {
  dialogIsOpen: boolean;
  dialogToggle: () => void;
  trailerUrl: string;
  eventBus: EventBus<DashboardEvents>;
}) => {
  return (
    <Dialog maxWidth="md" open={dialogIsOpen} fullWidth>
      <DialogTitle
        variant="h5"
        component="h5"
        className="flex justify-between text-text-primary relative"
      >
        <span className="hidden md:block">{title}</span>

        <div className="h-[24px] w-[24px] md:hidden" />
        <Chip
          label={<Typography variant="button-s">{adaptCourseTypeText(courseType)}</Typography>}
          size="small"
          sx={{
            color: colorScheme.courseTypeTag.text,
            backgroundColor: colorScheme.courseTypeTag.bg,
          }}
          variant="filled"
          className="md:hidden flex m-auto"
        />

        <CancelRounded className="text-action-divider" onClick={dialogToggle} />
      </DialogTitle>

      <DialogContent>
        <ModalVideo
          trailerPlaybackId={trailerPlaybackId}
          trailerPlaybackToken={trailerPlaybackToken}
          placeholderImg={imageSrc || '/assets/images/card-placeholder.png'}
        />
        <div className="hidden md:block">
          <Grid container spacing={4}>
            <Grid size={{ xs: 12, md: 8 }}>
              <ModalDescription
                description={description}
                lessons={lessons}
                tagColor={colorScheme?.courseTypeTag}
                tagText={courseType}
              />
              {courseType?.toLowerCase() !== 'masterclass' && (
                <LessonList lessonCount={videoCount} lessons={lessons || []} />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <ModalMetadata
                documentId={documentId}
                eventBus={eventBus}
                key={Math.random()}
                videoCount={videoCount}
                level={level}
                genres={genres}
                duration={duration}
                instructors={instructors}
                courseType={courseType}
                highlighted={undefined}
              />
            </Grid>
          </Grid>
        </div>
        <div className="md:hidden">
          <DialogContentMobile
            description={description}
            progressValue={coursePercentage}
            title={title}
            key={Math.random()}
            videoCount={videoCount}
            level={level}
            genres={genres}
            duration={duration}
            lessons={lessons}
            instructors={instructors}
            courseType={courseType}
            highlighted={undefined}
          >
            {courseType?.toLowerCase() !== 'masterclass' && (
              <LessonList lessonCount={videoCount} lessons={lessons || []} />
            )}
          </DialogContentMobile>
        </div>
      </DialogContent>
      <DialogActions>
        <Button
          size="medium"
          variant="contained"
          color="primary"
          className="self-stretch my-auto w-full md:w-auto"
          onClick={() =>
            eventBus.emit(DashboardEventKeys.redirects.to, {
              url: routes.seriesDetails.makeUrl(documentId, title),
              isExternalPage: false,
            })
          }
        >
          <PlayArrowRounded /> <Typography variant="button-m">Guarda ora</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};
